import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../context/UIContext';
import { getAllUsers } from '../services/UserService';
import { getAllCompanies } from '../services/CompanyService';
import {
    createServiceReport,
    getBookedVisits,
    getServiceReportPdf,
    updateServiceReport,
} from '../services/ServiceReportService';
import { getCurrentUserId } from '../utils/authHelper';
import { extractApiError } from '../utils/apiError';
import { formatDate } from '../utils/dateConverter';
import { closePendingTab, openPendingTab, revealBlobInTab } from '../utils/pdfTab';
import {
    bookedToVisit,
    emptyVisit,
    fmtDuration,
    formToPayload,
    nextDay,
    periodError,
    periodOfVisits,
    reportToForm,
    todayIso,
    visitMinutes,
    visitTotals,
    visitsError,
} from '../utils/serviceReport';
import '../styles/Invoices.css';
import '../styles/ServiceReports.css';

function blankForm() {
    return {
        title: '',
        technician_id: getCurrentUserId() || '',
        location: '',
        contact_person: '',
        work_description: '',
        remarks: '',
        language: 'de',
        visits: [],
    };
}

function periodHint({ form, period, booked, people }) {
    const person = people.find(p => String(p.id) === String(form.technician_id));
    const who = person ? ` von ${person.name || person.username}` : '';
    if (!form.technician_id) return { text: 'Techniker wählen, um die gebuchten Zeiten zu übernehmen.' };
    const problem = periodError(period.from, period.to);
    if (problem) return { text: problem, error: true };
    if (booked.state === 'error') return { text: booked.message, error: true };
    if (booked.state === 'kept') {
        return { text: 'Gespeicherte Zeiten. Zeitraum ändern oder „Neu übernehmen“ lädt sie aus der Zeiterfassung.' };
    }
    if (booked.state !== 'done') return { text: 'Gebuchte Zeiten werden geladen…' };
    const days = Object.keys(booked.byDate).length;
    if (days === 0) {
        return { text: `Keine Buchungen${who} auf diesem Projekt im Zeitraum. In der Zeiterfassung buchen oder einen Tag manuell eintragen.` };
    }
    return { text: `${days} gebuchte${days === 1 ? 'r Tag' : ' Tage'}${who} übernommen. Änderungen hier betreffen nur den Bericht.` };
}

/** Create or edit a Servicebericht. `report` null is a new one for `project`. */
export default function ServiceReportModal({ project, report = null, onClose, onSaved, onDelete }) {
    const toast = useToast();
    const isEdit = !!report;
    const [form, setForm] = useState(() => (report ? reportToForm(report) : blankForm()));
    const [people, setPeople] = useState([]);
    const [saving, setSaving] = useState(false);
    const [period, setPeriod] = useState(() => (report
        ? periodOfVisits(report.visits, todayIso())
        : { from: todayIso(), to: todayIso() }));
    // A saved report keeps its times until the period or the technician is changed.
    const [autoFill, setAutoFill] = useState(!isEdit);
    const [booked, setBooked] = useState({ state: isEdit ? 'kept' : 'idle', byDate: {}, message: '' });
    const lookup = useRef(0);

    useEffect(() => {
        getAllUsers()
            .then(users => setPeople((users || []).filter(u => u.is_active !== false)))
            .catch(() => setPeople([]));
    }, []);

    // A new report starts with the customer's address and contact; both stay editable.
    useEffect(() => {
        if (isEdit || !project?.companyId) return;
        getAllCompanies()
            .then(companies => {
                const company = (companies || []).find(c => String(c.id) === String(project.companyId));
                if (!company) return;
                setForm(f => ({
                    ...f,
                    location: f.location || company.address || '',
                    contact_person: f.contact_person || company.contactPerson || '',
                }));
            })
            .catch(() => {});
    }, [isEdit, project?.companyId]);

    const loadBooked = useCallback(async (from, to, technicianId) => {
        const id = ++lookup.current;
        setBooked(b => ({ ...b, state: 'loading' }));
        try {
            const rows = await getBookedVisits(project.id, { technicianId, startDate: from, endDate: to });
            if (id !== lookup.current) return;
            setForm(f => ({ ...f, visits: rows.map(bookedToVisit) }));
            setBooked({ state: 'done', byDate: Object.fromEntries(rows.map(r => [r.date, r])), message: '' });
        } catch (err) {
            if (id !== lookup.current) return;
            setBooked({
                state: 'error',
                byDate: {},
                message: extractApiError(err, 'Die gebuchten Zeiten konnten nicht geladen werden.'),
            });
        }
    }, [project?.id]);

    useEffect(() => {
        // Bumped even when nothing is fetched, so an older answer cannot land afterwards.
        lookup.current += 1;
        if (!autoFill || !form.technician_id || periodError(period.from, period.to)) return undefined;
        const timer = setTimeout(() => loadBooked(period.from, period.to, form.technician_id), 250);
        return () => clearTimeout(timer);
    }, [autoFill, period.from, period.to, form.technician_id, loadBooked]);

    const set = (key, value) => setForm(f => ({ ...f, [key]: value }));

    const changePeriod = (key, value) => {
        setAutoFill(true);
        setPeriod(p => ({ ...p, [key]: value }));
    };

    const changeTechnician = (value) => {
        setAutoFill(true);
        set('technician_id', value);
    };

    const setVisit = (index, key, value) => setForm(f => ({
        ...f,
        visits: f.visits.map((v, i) => (i === index ? { ...v, [key]: value } : v)),
    }));

    const addVisit = () => setForm(f => {
        const last = f.visits[f.visits.length - 1];
        const next = last
            ? { ...last, date: nextDay(last.date || todayIso()) }
            : emptyVisit(period.from || todayIso());
        return { ...f, visits: [...f.visits, next] };
    });

    const removeVisit = index => setForm(f => ({
        ...f,
        visits: f.visits.filter((_, i) => i !== index),
    }));

    const validate = () => {
        if (!form.title.trim()) return 'Bitte den Anlass angeben';
        return visitsError(form.visits);
    };

    const persist = async () => {
        const payload = formToPayload(form);
        return isEdit
            ? updateServiceReport(report.id, payload)
            : createServiceReport({ ...payload, project_id: project.id });
    };

    const save = async (e) => {
        e?.preventDefault();
        const problem = validate();
        if (problem) { toast(problem); return; }
        setSaving(true);
        try {
            const saved = await persist();
            toast(isEdit ? 'Servicebericht gespeichert.' : `Servicebericht ${saved.report_number} angelegt.`);
            onSaved(saved);
        } catch (err) {
            toast(extractApiError(err, 'Servicebericht konnte nicht gespeichert werden.'));
        } finally {
            setSaving(false);
        }
    };

    const saveAndOpenPdf = async () => {
        const problem = validate();
        if (problem) { toast(problem); return; }
        // Opened before the first await, or Safari and Firefox block it.
        const tab = openPendingTab('Servicebericht wird erstellt…');
        setSaving(true);
        let saved = null;
        try {
            saved = await persist();
            if (tab) {
                revealBlobInTab(tab, await getServiceReportPdf(saved.id));
            } else {
                toast('Gespeichert. Das PDF wurde vom Browser blockiert — bitte Popups erlauben.');
            }
            onSaved(saved);
        } catch (err) {
            closePendingTab(tab);
            toast(extractApiError(err, saved
                ? 'Gespeichert, aber das PDF konnte nicht erstellt werden.'
                : 'Servicebericht konnte nicht gespeichert werden.'));
            if (saved) onSaved(saved);
        } finally {
            setSaving(false);
        }
    };

    const totals = visitTotals(form.visits);
    const canLoad = !!form.technician_id && !periodError(period.from, period.to);
    const hint = periodHint({ form, period, booked, people });

    const reload = () => {
        if (!autoFill) { setAutoFill(true); return; }
        if (canLoad) loadBooked(period.from, period.to, form.technician_id);
    };

    return (
        <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
            <form className="inv-modal sr-modal" onSubmit={save} onClick={e => e.stopPropagation()}>
                <div className="inv-modal-header">
                    <div className="inv-modal-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14.7 6.3a4 4 0 00-5.4 5.4L3 18v3h3l6.3-6.3a4 4 0 005.4-5.4l-2.5 2.5-2.4-.6-.6-2.4z"/>
                        </svg>
                    </div>
                    <div>
                        <h2 className="inv-modal-title">
                            {isEdit ? `Servicebericht ${report.report_number}` : 'Neuer Servicebericht'}
                        </h2>
                        <p className="inv-modal-sub">
                            {project?.name}{project?.companyName ? ` · ${project.companyName}` : ''}
                        </p>
                    </div>
                    <button type="button" className="inv-modal-close" onClick={onClose} aria-label="Schließen">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12"/>
                        </svg>
                    </button>
                </div>

                <div className="inv-modal-body">
                    <p className="inv-section-title">Einsatz</p>
                    <div className="inv-grid-2">
                        <div className="inv-field full">
                            <label className="inv-label" htmlFor="sr-title">Anlass *</label>
                            <input id="sr-title" className="inv-input" value={form.title}
                                placeholder="z. B. Störung Linie 3, Inbetriebnahme, Wartung"
                                onChange={e => set('title', e.target.value)} />
                        </div>
                        <div className="inv-field">
                            <label className="inv-label" htmlFor="sr-tech">Techniker</label>
                            <select id="sr-tech" className="inv-select" value={form.technician_id}
                                onChange={e => changeTechnician(e.target.value)}>
                                <option value="">– Kein Techniker –</option>
                                {people.map(p => (
                                    <option key={p.id} value={String(p.id)}>{p.name || p.username}</option>
                                ))}
                            </select>
                        </div>
                        <div className="inv-field">
                            <label className="inv-label" htmlFor="sr-contact">Ansprechpartner beim Kunden</label>
                            <input id="sr-contact" className="inv-input" value={form.contact_person}
                                onChange={e => set('contact_person', e.target.value)} />
                        </div>
                        <div className="inv-field">
                            <label className="inv-label" htmlFor="sr-location">Einsatzort</label>
                            <input id="sr-location" className="inv-input" value={form.location}
                                onChange={e => set('location', e.target.value)} />
                        </div>
                        <div className="inv-field">
                            <label className="inv-label" htmlFor="sr-language">Sprache des PDF</label>
                            <select id="sr-language" className="inv-select" value={form.language}
                                onChange={e => set('language', e.target.value)}>
                                <option value="de">Deutsch</option>
                                <option value="en">English</option>
                            </select>
                        </div>
                    </div>

                    <p className="inv-section-title">Einsatzzeiten</p>
                    <div className="sr-period">
                        <div className="inv-field">
                            <label className="inv-label" htmlFor="sr-from">Zeitraum von</label>
                            <input id="sr-from" type="date" className="inv-input" value={period.from}
                                onChange={e => changePeriod('from', e.target.value)} />
                        </div>
                        <div className="inv-field">
                            <label className="inv-label" htmlFor="sr-to">bis</label>
                            <input id="sr-to" type="date" className="inv-input" value={period.to}
                                min={period.from || undefined}
                                onChange={e => changePeriod('to', e.target.value)} />
                        </div>
                        <button type="button" className="inv-btn sr-reload" onClick={reload}
                            disabled={!canLoad || booked.state === 'loading'}
                            title="Zeiten erneut aus der Zeiterfassung laden">
                            Neu übernehmen
                        </button>
                    </div>
                    <p className={`sr-period-hint${hint.error ? ' sr-period-hint--error' : ''}`}
                        data-testid="sr-period-hint">
                        {hint.text}
                    </p>

                    {form.visits.length > 0 && (
                        <div className="sr-visits">
                            <div className="sr-visit sr-visit--head" aria-hidden="true">
                                <span>Datum</span><span>Ankunft</span><span>Abfahrt</span>
                                <span>Pause (min)</span><span>Anfahrt (min)</span><span>Aufenthalt</span><span />
                            </div>
                            {form.visits.map((v, i) => {
                                const { onSite, work } = visitMinutes(v);
                                const source = booked.byDate[v.date];
                                return (
                                    <React.Fragment key={i}>
                                        <div className="sr-visit" data-testid={`sr-visit-${i}`}>
                                            <label className="sr-cell">
                                                <span className="sr-cell-label">Datum</span>
                                                <input type="date" className="inv-input" value={v.date}
                                                    onChange={e => setVisit(i, 'date', e.target.value)} />
                                            </label>
                                            <label className="sr-cell">
                                                <span className="sr-cell-label">Ankunft</span>
                                                <input type="time" className="inv-input" value={v.start_time}
                                                    onChange={e => setVisit(i, 'start_time', e.target.value)} />
                                            </label>
                                            <label className="sr-cell">
                                                <span className="sr-cell-label">Abfahrt</span>
                                                <input type="time" className="inv-input" value={v.end_time}
                                                    onChange={e => setVisit(i, 'end_time', e.target.value)} />
                                            </label>
                                            <label className="sr-cell">
                                                <span className="sr-cell-label">Pause (min)</span>
                                                <input type="number" min="0" step="5" className="inv-input"
                                                    value={v.break_minutes}
                                                    onChange={e => setVisit(i, 'break_minutes', e.target.value)} />
                                            </label>
                                            <label className="sr-cell">
                                                <span className="sr-cell-label">Anfahrt (min)</span>
                                                <input type="number" min="0" step="5" className="inv-input"
                                                    value={v.travel_minutes}
                                                    onChange={e => setVisit(i, 'travel_minutes', e.target.value)} />
                                            </label>
                                            <span className="sr-cell sr-duration" title="Aufenthalt / Arbeitszeit ohne Pause">
                                                <span className="sr-cell-label">Aufenthalt</span>
                                                <strong>{fmtDuration(onSite)}</strong>
                                                <small>Arbeit {fmtDuration(work)}</small>
                                            </span>
                                            <button type="button" className="inv-item-del sr-visit-del"
                                                onClick={() => removeVisit(i)}
                                                aria-label="Tag entfernen" title="Tag entfernen">
                                                ×
                                            </button>
                                        </div>
                                        {source?.partial && (
                                            <p className="sr-visit-note" data-testid={`sr-visit-note-${i}`}>
                                                Am {formatDate(v.date)} auch auf andere Projekte gebucht
                                                ({fmtDuration(source.project_minutes)} auf diesem Projekt) –
                                                Ankunft und Abfahrt gelten für den ganzen Arbeitstag, bitte prüfen.
                                            </p>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    )}
                    <div className="sr-visits-foot">
                        <button type="button" className="inv-btn" onClick={addVisit}>+ Tag manuell</button>
                        <span className="sr-totals" data-testid="sr-totals">
                            Aufenthalt <strong>{fmtDuration(totals.onSite)}</strong>
                            {' · '}Arbeitszeit <strong>{fmtDuration(totals.work)}</strong>
                            {totals.travel > 0 && <>{' · '}Anfahrt <strong>{fmtDuration(totals.travel)}</strong></>}
                        </span>
                    </div>

                    <p className="inv-section-title">Bericht</p>
                    <div className="inv-grid-2">
                        <div className="inv-field full">
                            <label className="inv-label" htmlFor="sr-work">Durchgeführte Arbeiten</label>
                            <textarea id="sr-work" className="inv-textarea" rows={8}
                                value={form.work_description}
                                onChange={e => set('work_description', e.target.value)} />
                        </div>
                        <div className="inv-field full">
                            <label className="inv-label" htmlFor="sr-remarks">Ergebnis / offene Punkte</label>
                            <textarea id="sr-remarks" className="inv-textarea" rows={3}
                                value={form.remarks}
                                onChange={e => set('remarks', e.target.value)} />
                        </div>
                    </div>
                </div>

                <div className="inv-modal-footer">
                    {isEdit && onDelete && (
                        <button type="button" className="inv-btn danger sr-delete" disabled={saving} onClick={onDelete}>
                            Löschen
                        </button>
                    )}
                    <button type="button" className="inv-btn" onClick={onClose}>Abbrechen</button>
                    <button type="submit" className="inv-btn" disabled={saving}>Speichern</button>
                    <button type="button" className="inv-btn primary" disabled={saving} onClick={saveAndOpenPdf}>
                        Speichern &amp; PDF öffnen
                    </button>
                </div>
            </form>
        </div>
    );
}
